import * as React from 'react'
import { TabMenu } from 'primereact/tabmenu'
import { MenuItem } from 'primereact/menuitem'
import { Button } from 'primereact/button'
import InputCalendar, {
    InputCalendarProps,
} from 'app/shared/components/InputCalendar'

/*======================================================================
 * @ 업무설명 : 공용 기간 선택 달력 컴퍼넌트
 * @ 파 일 명 : /app/shared/components/CalendarSelect.tsx
 * @ 화면 ID : -
 * @ 프로그램ID : -
 * @Rest URI : -
 * @ASIS :
 *======================================================================
 * 번호  작업자           작업일         변경내용
 *======================================================================
 * 1     이민주(S26379)   2025.05.20    최초작성
 *======================================================================
 */

/**
 * 캘린더에서 선택된 기간을 반환하는 포맷이다.  onSearch(e:DateRange )
 */
export type DateRange = {
    /**
     * 선택된 시작일
     */
    start: Date
    /**
     * 선택된 종료일(또는 undefined)
     */
    end: Date
    /**
     * 선택된 시작일 문자열 YYYYMMDD
     */
    startStr?: string
    /**
     * 선택된 종료일 문자열 YYYYMMDD
     */
    endStr?: string
}

export type CalendarSelectProps = {
    /**
     * 현재부터 과거 날짜만 선택 가능하게 설정한다.
     */
    isPastOnly?: boolean
    /**
     * 서버 시간 등 로컬 시간이 아닌 시간을 오늘로 지정하는 경우에 사용, 없는 경우에는 로컬 시간으로 처리한다.
     * isPastOnly와 사용되는 경우 today 이후 날짜는 선택 불가능 하게 처리한다.
     */
    today?: Date
    /**
     * 활성화 할 기간 선택 버튼
     * 오늘: 'today'
     * 이번달: 'thismonth'
     * 1주: 'week'
     * 1개월: 'month'
     * 3개월: '3month'
     * 선택하지 않음: 'custom'
     */
    selected?: 'today' | 'thismonth' | 'week' | 'month' | '3month' | 'custom'
    /**
     * 캘린더 컨테이너 쪽에 적용되는 className이다. 이곳에 전달해도 primereact쪽에는 전달되지 않는다.
     */
    className?: string
    /**
     * 초기에 설정 시킬 특정 날짜가 있는 경우 지정한다.(selected 'custom'일 때 사용)
     */
    defaultDates?: DateRange
    /**
     * 시작일 캘린더에 전달할 Props를 지정한다. 중복되는 경우에도 본 Props에 명시된 Props가 우선 적용된다.
     */
    startCalendar?: InputCalendarProps
    /**
     * 종료일 캘린더에 전달할 Props를 지정한다. 중복되는 경우에도 본 Props에 명시된 Props가 우선 적용된다.
     */
    endCalendar?: InputCalendarProps
    /**
     * 조회 버튼을 눌렀을 때 콜백을 전달한다.
     */
    onSearch?: (seletedDates: DateRange) => void
}

/**
 * 기간 선택 캘린더
 * @param props
 * @returns
 */
const Calendar: React.FC<CalendarSelectProps> = (props) => {
    const {
        isPastOnly = true,
        today = new Date(),
        selected = 'today',
        className = '',
        defaultDates = { start: null, end: null },
        startCalendar,
        endCalendar,
        onSearch,
    } = props
    const selectedKey = ['today', 'thismonth', 'week', 'month', '3month']
    const [tabIndex, setTabIndex] = React.useState<number>(
        selectedKey.indexOf(selected)
    )
    /**
     * 시작 달력 선택 날짜, 기본 값 null
     */
    const [calendar1, setCalendar1] = React.useState<Date>(defaultDates.start)
    const calendar1ref = React.useRef<Date>(defaultDates.start)
    /**
     * 종료 달력 선택 날짜, 기본 값 null
     */
    const [calendar2, setCalendar2] = React.useState<Date>(defaultDates.end)
    const calendar2ref = React.useRef<Date>(defaultDates.end)
    /**
     * 시작일 최대 가능 날짜( <= 종료일)
     */
    const [startMaxDate, setStartMaxDate] = React.useState<Date>(
        defaultDates.end
    )
    /**
     * 종료일 최소 가능 날짜( >= 시작일)
     */
    const [endMinDate, setEndMinDate] = React.useState<Date>(defaultDates.start)

    const dateSelectTab: MenuItem[] = [
        {
            label: '오늘',
            command: () => {
                handleMinMaxDate(today, 'start')
                handleMinMaxDate(today, 'end')
            },
        },
        {
            label: '이번달',
            command: () => {
                const startDate = new Date(today)
                startDate.setDate(1)
                const endDate = new Date(today)
                endDate.setMonth(endDate.getMonth() + 1)
                endDate.setDate(0)
                handleMinMaxDate(startDate, 'start')
                handleMinMaxDate(endDate, 'end')
            },
        },
        {
            label: '1주일',
            command: () => {
                const prevWeek = new Date(today)
                prevWeek.setDate(today.getDate() - 7)
                handleMinMaxDate(prevWeek, 'start')
                handleMinMaxDate(today, 'end')
            },
        },
        {
            label: '1개월',
            command: () => {
                const prevMonth = new Date(today)
                prevMonth.setMonth(today.getMonth() - 1)
                handleMinMaxDate(prevMonth, 'start')
                handleMinMaxDate(today, 'end')
            },
        },
        {
            label: '3개월',
            command: () => {
                const prevMonth = new Date(today)
                prevMonth.setMonth(today.getMonth() - 3)
                handleMinMaxDate(prevMonth, 'start')
                handleMinMaxDate(today, 'end')
            },
        },
    ]

    const getDate2Str = (source: Date) => {
        return source
            .toLocaleDateString('ko', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            })
            .replace(/[\s.]/g, '')
    }

    const handleMinMaxDate = React.useCallback(
        (date: Date, type: 'start' | 'end') => {
            if (type === 'start') {
                setCalendar1(date)
                calendar1ref.current = date
                setEndMinDate(date)
                setStartMaxDate(calendar2ref.current)
                return
            }

            setCalendar2(date)
            calendar2ref.current = date
            setEndMinDate(calendar1ref.current)
            setStartMaxDate(date)
            //방어코드 추가 - 종료일을 변경했는데, 시작일이 그보다 이후면 시작일을 종료일로 맞춘다.
            if (date < calendar1ref.current) {
                setCalendar1(date)
            }
        },
        [calendar1, calendar2]
    )
    const handleSearch = React.useCallback(() => {
        const startStr = getDate2Str(calendar1)
        const endStr = getDate2Str(calendar2)
        onSearch({
            start: calendar1,
            end: calendar2,
            startStr: startStr,
            endStr: endStr,
        })
    }, [calendar1, calendar2])

    /**
     * 탭 비활성화가 필요해서 TabMenu컴퍼넌트 내부 렌더링을 무력화 시킴.
     * activeIndex가 생성 시에 사용되고 이후 -1을 전달해도 fallback됨.
     * 매번 새로 그리기 때문에 성능에는 좋지 않음.
     * @param param0
     * @returns
     */
    const ExTabMenu = ({ idx }) => {
        return (
            <TabMenu
                model={dateSelectTab}
                className="tabs-rounded2 -text"
                onTabChange={(e) => {
                    setTabIndex(e.index)
                }}
                activeIndex={idx}
            />
        )
    }

    React.useEffect(() => {
        if (tabIndex > -1) {
            dateSelectTab[tabIndex].command(null)
        }
    }, [])

    return (
        <div className={`calendar-select ${className}`.trim()}>
            <ExTabMenu idx={tabIndex} />
            <div className="inputs-x">
                <InputCalendar
                    {...startCalendar}
                    isPastOnly={isPastOnly}
                    today={today}
                    placeholder="시작일"
                    title="시작일 날짜입력"
                    maxDate={startMaxDate}
                    value={calendar1}
                    onChange={(e: Date) => {
                        handleMinMaxDate(e, 'start')
                    }}
                    calrendarProps={{
                        onVisibleChange: (e) => {
                            setTabIndex(-1)
                        },
                    }}
                />
                <span className="bar px-5">~</span>
                <InputCalendar
                    {...endCalendar}
                    isPastOnly={isPastOnly}
                    today={today}
                    placeholder="종료일"
                    title="종료일 날짜입력"
                    minDate={endMinDate}
                    value={calendar2}
                    onChange={(e: Date) => {
                        handleMinMaxDate(e, 'end')
                    }}
                    calrendarProps={{
                        onVisibleChange: (e) => {
                            setTabIndex(-1)
                        },
                    }}
                />
            </div>
            <Button
                label="조회"
                className="button-basic-tint -md width-full"
                onClick={handleSearch}
            />
        </div>
    )
}

export default Calendar
